import { Component, Host, h, Prop, getAssetPath, State } from '@stencil/core';
import { Consumes } from '@sydle/sydle-ui';
import { Entry } from '../../models/site';
import { getLanguagePath } from '../sy-site-i18n/static-i18n';
import { LanguageLink } from '../sy-site-language-buttons/sy-site-language-buttons';

export declare type Item = {
  title: string;
  link: string;
}

export declare type Category = {
  title: string;
  items: Array<Item>;
}

@Component({
  tag: 'sy-site-footer',
  styleUrl: './scss/index.scss',
  shadow: true,
  assetsDirs: ['assets'],
})
export class SySiteFooter {

  /**
   * Available languages in footer with fallback links in case alternate page is not available
   */
  @Prop() languages: LanguageLink[];

  /**
   * Link to privacy policy page
   */
  @Prop() policyLinks: Array<Item>;

  /**
   * Current year for copy right
   */
  @Prop() year = new Date().getFullYear();

  /**
  * Array of categories and items do be displayed in list
  */
  @Prop() categories: Array<Entry>;

  /**
  * Path to logo image
  */
  @Prop() pathImage = './assets/sydle-logo-white.svg';

  /**
  * Footer logo href
  */
  @State() logoHref = '/';
  @Consumes('i18next:language') @State() lang: string;

  @State() isMobile = window.screen.width <= 450;

  private wrapCategory(category: Entry): HTMLSyColElement[] {
    if (category.children[0].children) {
      return (
        <sy-cell width={category.colspan ? category.colspan : category.children.length}>
          <sy-grid type='grid' style={{ gridTemplateColumns: `repeat(auto-fit, minmax(8.6rem, 1fr))` }}>
            {category.children.map((child, i) => {
              return (
                <sy-cell key={i}>
                  <sy-site-footer-list category={i === 0 ? category.title : undefined} subCategory={child.title} items={child.children} />
                </sy-cell>
              )
            })
            }
          </sy-grid >
        </sy-cell>
      )
    }
    const wrap = new Array(category.colspan ? category.colspan : Math.ceil(category.children.length / 5)).fill([]);
    const colSize = category.colspan ? Math.ceil(category.children.length / category.colspan) : 5;
    return (
      <sy-cell width={wrap.length}>
        <sy-grid type='grid' style={{ gridTemplateColumns: `repeat(auto-fit, minmax(8.6rem, 1fr))` }}>
          {wrap.map((_, i) => {
            return (
              <sy-cell key={i}>
                <sy-site-footer-list category={i === 0 ? category.title : undefined} items={category.children.slice(i * colSize, i * colSize + colSize)} />
              </sy-cell>
            )
          })
          }
        </sy-grid >
      </sy-cell>
    )
  }

  private renderMobileCollapse(category: Entry) {
    return (
      <sy-cell>
        <sy-site-footer-list-collapse entry={category} />
      </sy-cell>
    )
  }

  private setFooterType(resizeQuery: MediaQueryList): void {
    if (resizeQuery.matches) this.isMobile = true;
    else this.isMobile = false;
  }


  componentWillLoad() {
    const resizeQuery = window.matchMedia('(max-width: 450px)');
    this.setFooterType(resizeQuery);
    resizeQuery.onchange = this.setFooterType.bind(this);
  }

  componentWillRender() {
    this.logoHref = window.location.origin + getLanguagePath(this.lang);
  }

  render() {
    return (
      <Host>
        <sy-grid type='flex'>
          <sy-row>
            <sy-col size-md={3} size-lg={2}>
              <div class="logo d-none d-sm-block">
                <img src={getAssetPath(this.pathImage)} onClick={() => window.location.assign(this.logoHref)} />
              </div>
            </sy-col>
            <sy-col size-md={9} size-lg={10}>
              {this.isMobile ? (
                <div class="collapse">
                  {this.categories?.map(this.renderMobileCollapse)}
                </div>
              ) : (
                <sy-grid type='grid' style={{ gridTemplateColumns: `repeat(auto-fit, minmax(8.6rem, 1fr))` }}>
                  {this.categories?.map(this.wrapCategory)}
                </sy-grid>
              )}
            </sy-col>
          </sy-row>
          <sy-row>
            <div class="footer-menu">
              <sy-site-language-buttons languages={this.languages}></sy-site-language-buttons>
              <sy-site-footer-copyright links={this.policyLinks}></sy-site-footer-copyright>
            </div>
          </sy-row>
        </sy-grid>
      </Host >
    );
  }

}
